import React, { useState, useEffect } from 'react';
import {
  useMemorizeFilters,
  POSSIBLE_FILTERS_ENTITIES
} from 'hooks/useMemorizeInputsFilters';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Card,
  Form,
  Col,
  CardHeader,
  Button,
  CardBody,
  Label
} from 'reactstrap';

import QueueService from '../../../Queue/service';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import FlexSelect from 'components/Inputs/FlexSelect';
import Loader from 'react-loader-spinner';
import HourMaskMMHHSS from '../../../../components/Inputs/HourMaskMMHHSS.js';
import DatePicker from '../../../../components/Inputs/DatePicker.js';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import ChatBotReportService from './service';
import Pagination from 'components/Pagination';

import {
  dateRules,
  hourRules,
  queueNameId,
  toISOFormat,
  watsonToId
} from './utils';
import TableAnalytical from './tables/Analytical';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { ClearFiltersButton } from 'components/ClearFiltersButton';

const errorFormMessage = (message) => <p style={{ color: 'red' }}>{message}</p>;

const formatDates = (date, time) => {
  const [day, month, year] = date.split('/');
  const [hour, minute, second] = time.split(':');
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
};

const ChatBotReport = () => {
  const { control, handleSubmit, errors, setValue, watch, getValues } =
    useForm();
  const { getMemorizedFilters, memorizeFilters, clearMemorizedFilters } =
    useMemorizeFilters(POSSIBLE_FILTERS_ENTITIES.REPORT_ADVANCED_CHATBOT);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [watsonList, setWatsonList] = useState([]);
  const [optionsWatson, setOptionsWatson] = useState([]);
  const [queueShowDisable, setQueueShowDisable] = useState(false);
  const [showWatsonDisable, setShowWatsonDisable] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [quantity, setQuantity] = useState(0);

  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const optionWatson = watch('watson');

  async function loadTableConversation(pagina, override = null) {
    setLoadingReport(true);

    const params = override || {
      finalDate: formatDates(watch('dateBegin'), watch('timeStart')),
      initialDate: formatDates(watch('dateEnd'), watch('timeEnd')),
      watsonId: optionWatson,
      options: getValues()['watsonOption'],
      queueId: getValues()['queue'],
      page: pagina,
      limit: 10
    };

    const response = await ChatBotReportService.getHistoryWatson(params);

    if (response?.data?.data.length === 0) {
      toast.info('Nenhum registro encontrado!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }

    setDataTable(response?.data?.data);
    setQuantity(response?.data?.totalFiltered);
    setLoadingReport(false);
  }

  const handlePageChange = async (page) => {
    const prevFilters = getMemorizedFilters();
    memorizeFilters({ ...prevFilters, page });
    setActivePage(page);

    const loadTableParams = {
      finalDate: formatDates(prevFilters?.dateEnd, prevFilters?.timeEnd),
      initialDate: formatDates(prevFilters?.dateBegin, prevFilters?.timeStart),
      watsonId: optionWatson,
      options: getValues()['watsonOption'],
      queueId: getValues()['queue'],
      showAllWatson: showWatsonDisable,
      page: page,
      limit: 10
    };
    loadTableConversation(page, loadTableParams);
  };

  useEffect(() => {
    if (!optionWatson) return;
    ChatBotReportService.getOptionsWatson(optionWatson).then((response) => {
      const options = response?.data.data.map((option) => ({
        label: option,
        value: option
      }));
      setOptionsWatson(options ?? []);
    });
  }, [optionWatson]);

  useEffect(() => {
    QueueService.getAllShowDisable(queueShowDisable)
      .then((response) => {
        setQueues(response.data.data);
      })
      .catch((err) => {
        console.log('err: ' + err);
      });
  }, [queueShowDisable]);

  useEffect(() => {
    getWatson({
      showDisable: showWatsonDisable
    });
  }, [showWatsonDisable]);

  useEffect(() => {
    if (watsonList.length > 0) {
      const formValues = getValues();
      onSubmit(formValues, false);
    }
  }, [watsonList]);

  const getWatson = ({ showDisable = true }) => {
    ChatBotReportService.getWatson({
      showDisable
    })
      .then((response) => {
        setLoading(false);
        setWatsonList(response?.data.data ?? []);
      })
      .catch((error) => {
        if (error.response) {
          toast.error('Erro ao carregar Watson!', {
            autoClose: 3000,
            closeOnClick: true
          });
        }
      });
  };

  const handleReset = () => {
    const defaultFieldValues = {
      watson: '',
      watsonOption: '',
      queue: '',
      dateBegin: new Date().toLocaleDateString('pt-br'),
      timeStart: '00:00:00',
      dateEnd: new Date().toLocaleDateString('pt-br'),
      timeEnd: '23:59:59'
    };

    for (const key in defaultFieldValues) {
      setValue(`${key}`, defaultFieldValues[key]);
    }

    clearMemorizedFilters();
  };

  const onSubmit = async (data, isNotFromUseEffect = true) => {
    try {
      let pageSavedOnMemory = 1;

      if (!isNotFromUseEffect) {
        pageSavedOnMemory = getMemorizedFilters()?.page ?? 1;
      }

      setActivePage(pageSavedOnMemory);
      memorizeFilters({ ...data, page: pageSavedOnMemory });

      setLoadingReport(true);
      const response = await ChatBotReportService.getHistoryWatson({
        finalDate: formatDates(data?.dateEnd, data?.timeEnd),
        initialDate: formatDates(data?.dateBegin, data?.timeStart),
        watsonId: optionWatson,
        options: getValues()['watsonOption'],
        queueId: getValues()['queue'],
        showAllWatson: showWatsonDisable,
        page: pageSavedOnMemory,
        limit: 10
      });

      if (response?.data?.data.length === 0) {
        toast.info('Nenhum registro encontrado!', {
          autoClose: 3000,
          closeOnClick: true
        });
      }

      setDataTable(response?.data?.data);
      setQuantity(response?.data?.totalFiltered);
      setLoadingReport(false);
    } catch (err) {
      console.log(err);
      setLoadingReport(false);
      toast.error(err || 'Ops, ocorreu um erro!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        showBackArrow="not"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3>Relatório de Chatbot</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <Loader
                        type="TailSpin"
                        color={systemConfiguration?.primary_color}
                        height={100}
                        width={100}
                      />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <ClearFiltersButton handleOnClick={handleReset} />
                      <Row className="mb-3">
                        <Col md="4">
                          <label className="form-control-label">Watson</label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={watsonList}
                                isMulti={true}
                                isClearable={true}
                                value={props.value}
                                closeMenuOnSelect={true}
                                valueController={setValue}
                                fieldName="watson"
                                labelName="nome_canal"
                                valueName="id"
                              />
                            )}
                            control={control}
                            name="watson"
                            defaultValue={getMemorizedFilters()?.watson ?? ''}
                          />
                        </Col>
                        <Col sm="4">
                          <label className="form-control-label">
                            Opção do Fluxo
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={optionsWatson}
                                isMulti={true}
                                value={props.value}
                                valueController={setValue}
                                closeMenuOnSelect={true}
                                isClearable={true}
                                fieldName="watsonOption"
                                labelName="label"
                                valueName="value"
                                isDisabled={
                                  optionWatson && optionWatson?.length > 0
                                    ? false
                                    : true
                                }
                              />
                            )}
                            control={control}
                            name="watsonOption"
                            defaultValue={
                              getMemorizedFilters()?.watsonOption ?? ''
                            }
                          />
                        </Col>
                        <Col md="4">
                          <label className="form-control-label">Fila</label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={queues}
                                isMulti={true}
                                isClearable={true}
                                value={props.value}
                                valueController={setValue}
                                closeMenuOnSelect={true}
                                fieldName="queue"
                                labelName="name"
                                valueName="id"
                              />
                            )}
                            control={control}
                            name="queue"
                            defaultValue={getMemorizedFilters()?.queue ?? ''}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Data inicial*
                          </label>
                          <Controller
                            render={(props) => (
                              <DatePicker
                                fieldName="dateBegin"
                                valueController={setValue}
                                defaultValue={props.value}
                              />
                            )}
                            control={control}
                            name="dateBegin"
                            rules={dateRules}
                            defaultValue={
                              getMemorizedFilters()?.dateBegin ??
                              new Date().toLocaleDateString('pt-br')
                            }
                          />
                          <ErrorMessage
                            errors={errors}
                            name="dateBegin"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Hora inicial*
                          </label>
                          <Controller
                            render={(props) => (
                              <HourMaskMMHHSS
                                valueController={setValue}
                                fieldName="timeStart"
                                value={props.value}
                              />
                            )}
                            control={control}
                            name="timeStart"
                            rules={hourRules}
                            defaultValue={
                              getMemorizedFilters()?.timeStart ?? '00:00:00'
                            }
                          />
                          <ErrorMessage
                            errors={errors}
                            name="timeStart"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">
                            Data final*
                          </label>
                          <Controller
                            render={(props) => (
                              <DatePicker
                                fieldName="dateEnd"
                                valueController={setValue}
                                defaultValue={props.value}
                              />
                            )}
                            control={control}
                            name="dateEnd"
                            defaultValue={
                              getMemorizedFilters()?.dateEnd ??
                              new Date().toLocaleDateString('pt-br')
                            }
                            rules={{
                              ...dateRules,
                              validate: (value) => {
                                if (
                                  new Date(toISOFormat(value, true)) <
                                  new Date(
                                    toISOFormat(getValues()['dateBegin'], true)
                                  )
                                ) {
                                  return 'A data final não pode ser anterior à inicial!';
                                } else {
                                  return true;
                                }
                              }
                            }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="dateEnd"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Hora final*
                          </label>
                          <Controller
                            render={(props) => (
                              <HourMaskMMHHSS
                                valueController={setValue}
                                fieldName="timeEnd"
                                value={props.value}
                              />
                            )}
                            control={control}
                            name="timeEnd"
                            rules={hourRules}
                            defaultValue={
                              getMemorizedFilters()?.timeEnd ?? '23:59:59'
                            }
                          />
                          <ErrorMessage
                            errors={errors}
                            name="timeEnd"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignContent: 'flex-start'
                          }}
                        >
                          <Label>Mostrar desabilitados:</Label>
                          <div>
                            <div>
                              <ToggleSwitch
                                checked={queueShowDisable}
                                id="showDisableQueueReportChatBot"
                                onChange={() =>
                                  setQueueShowDisable(!queueShowDisable)
                                }
                              />
                              <Label>Filas</Label>
                            </div>
                            <div>
                              <ToggleSwitch
                                checked={showWatsonDisable}
                                id="showDisableWatsonReportChatBot"
                                onChange={() =>
                                  setShowWatsonDisable(!showWatsonDisable)
                                }
                              />
                              <Label>Watson</Label>
                            </div>
                          </div>
                        </div>
                      </Row>
                      <hr />
                      <Button
                        style={buttonStyle(systemConfiguration?.primary_color)}
                        type="submit"
                      >
                        Buscar
                      </Button>
                    </Form>
                  )}

                  {loadingReport ? (
                    <Row className="justify-content-md-center">
                      <Loader
                        type="TailSpin"
                        color={systemConfiguration?.primary_color}
                        height={100}
                        width={100}
                      />
                    </Row>
                  ) : (
                    dataTable?.length > 0 && (
                      <>
                        <TableAnalytical
                          data={dataTable}
                          watson={watsonToId(watsonList)}
                          queues={queueNameId(queues)}
                          totalRegister={quantity}
                        />
                        <Pagination
                          activePage={activePage}
                          totalItemsCount={quantity}
                          itemsCountPerPage={10}
                          onChange={handlePageChange.bind(this)}
                        />
                      </>
                    )
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default ChatBotReport;
